import { NgTemplateOutlet } from '@angular/common';
import type { SimpleChanges, TemplateRef } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
} from '@angular/core';

import { CollapseState } from '@recruitee/common';
import { TranslatePipe } from '@recruitee/i18n';
import {
  DividerComponent,
  DateComponent,
  OfferStatusIndicatorComponent,
  IconComponent,
  AvatarComponent,
} from '@recruitee/user-interface';

import type {
  DecoratedGroupedOpenQuestionAnswers,
  GroupedOpenQuestionAnswer,
  OpenQuestionAnswer,
} from '../candidate-questions.interfaces';
import type { CandidateQuestionsModuleTranslations } from '../translations';
import { CandidateQuestionsTranslations } from '../translations';
import { QuestionsMode } from '../types';
import { CandidateQuestionsItemComponent } from './candidate-questions-item/candidate-questions-item.component';

const getCollapsedValue = (index: number) =>
  index === 0 ? CollapseState.Normal : CollapseState.Collapsed;

const assignCollapsed = (
  question: GroupedOpenQuestionAnswer,
  index: number,
  collapsed?: string,
) => {
  const newCollapsedValue = collapsed ? collapsed : getCollapsedValue(index);
  return {
    question,
    collapsed: newCollapsedValue,
  };
};

const assignStatusWhenOfferExist = (
  item: ReturnType<typeof assignCollapsed>,
): ReturnType<typeof assignCollapsed> & { decoratedStatus?: string | null } =>
  item.question.offer
    ? { ...item, decoratedStatus: item.question.offer ? item.question.offer.status : 'archived' }
    : item;

@Component({
  selector: 'rt-candidate-questions-results',
  templateUrl: './candidate-questions-results.component.html',
  styleUrls: ['./candidate-questions-results.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AvatarComponent,
    IconComponent,
    OfferStatusIndicatorComponent,
    NgTemplateOutlet,
    DateComponent,
    CandidateQuestionsItemComponent,
    DividerComponent,
    TranslatePipe,
  ],
})
export class CandidateQuestionsResultsComponent {
  public readonly QuestionsMode: typeof QuestionsMode = QuestionsMode;

  @Input() public visibilityTplRef: TemplateRef<any>;
  @Input() public data: GroupedOpenQuestionAnswer[];
  @Input() public canManageCandidates: boolean = false;
  @Input() public hasBooleanFlagAnswers: boolean = false;
  @Input() public mode: QuestionsMode;

  public CollapseState: typeof CollapseState = CollapseState;
  public groups: DecoratedGroupedOpenQuestionAnswers[];

  constructor(
    private cdRef: ChangeDetectorRef,
    @Inject(CandidateQuestionsTranslations)
    public translations: CandidateQuestionsModuleTranslations,
  ) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if ('data' in changes) {
      const previousGroups = this.groups;
      this.groups = this.data
        .map((question, index) => {
          if (!previousGroups || !previousGroups.length) return assignCollapsed(question, index);
          const previousGroupCollapsedValue = this.groups.find(
            group => group.question.offer?.id === question.offer?.id,
          )?.collapsed;
          return assignCollapsed(question, index, previousGroupCollapsedValue);
        })
        .map(assignStatusWhenOfferExist);
    }
  }

  public handleGroupClick(target: DecoratedGroupedOpenQuestionAnswers): void {
    if (this.canCollapsed(target)) {
      this.groups = this.groups.map(question =>
        question === target && this.canCollapsed(target)
          ? {
              ...question,
              collapsed:
                question.collapsed === CollapseState.Collapsed
                  ? CollapseState.Normal
                  : CollapseState.Collapsed,
            }
          : question,
      );

      this.cdRef.detectChanges();
    }
  }

  public getGroupTitle(group: DecoratedGroupedOpenQuestionAnswers): string {
    const offerTitle = group.question.offer && group.question.offer.title;
    return offerTitle || '';
  }

  public hasTitleGroup(group: DecoratedGroupedOpenQuestionAnswers): boolean {
    return !!this.getGroupTitle(group);
  }

  public canCollapsed(group: DecoratedGroupedOpenQuestionAnswers): boolean {
    return !!group.collapsed && this.groups.length > 1;
  }

  public groupsTrackBy(
    index: number,
    group: DecoratedGroupedOpenQuestionAnswers,
  ): number | OpenQuestionAnswer {
    return group.question.openQuestionAnswers[0];
  }

  public answersTrackBy(index: number, answer: OpenQuestionAnswer): number {
    return answer.id;
  }
}
